import type { IFileData } from '../types/IFileData';

/**
 * Через сколько освобождать object-URL после клика по ссылке. Сразу после `click()` нельзя: Safari и Firefox
 * читают Blob уже после него, и отозванный URL обрывает скачивание.
 */
const DOWNLOAD_URL_TTL_MS = 40_000;
/** Открытой вкладке URL нужен для рендера PDF — держим его дольше. */
const OPENED_URL_TTL_MS = 60_000;

/**
 * Единая точка скачивания Blob: создаёт object-URL, кликает по скрытой ссылке
 * и освобождает URL (revokeObjectURL) с задержкой, исключая утечку памяти.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_TTL_MS);
    }
}

/** Отказ сервера: страница ошибки или JSON с текстом отказа не должны сохраниться файлом. */
const responseError = (res: Response) =>
    Object.assign(new Error(`Файл не получен (код ${res.status}).`), { status: res.status });

/**
 * Скачать вложение по ссылке (`id`) или data-URL (`base64`): PDF открывается в новой вкладке, остальное сохраняется
 * файлом. `onError` получает отказ — сеть или ответ не 2xx (у ошибки есть `status`); сообщение показывает
 * вызывающий (у пакета нет своих уведомлений).
 */
export const downloadWithLoading = (
    { file, base64, id }: IFileData,
    setLoading: (f: boolean) => void,
    onError: (error: unknown) => void,
) => {
    const url = id || base64;
    setLoading(true);
    fetch(url)
        .then((res) => {
            if (!res.ok) throw responseError(res);
            return res.blob();
        })
        .then((blob: Blob) => {
            const ext = file.name.split('.').pop()?.toLowerCase();

            if (ext === 'pdf') {
                const pdfBlob = blob.type === 'application/pdf' ? blob : blob.slice(0, blob.size, 'application/pdf');
                const fileUrl = URL.createObjectURL(pdfBlob);
                window.open(fileUrl, '_blank');
                setTimeout(() => URL.revokeObjectURL(fileUrl), OPENED_URL_TTL_MS);
            } else {
                triggerBlobDownload(blob, file.name);
            }
        })
        .catch((error: unknown) => onError(error))
        .finally(() => {
            setLoading(false);
        });
};
